import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import DataTable, { Column } from "../DataTable";
import ModalLayout from "../layouts/ModalLayout";
import CustomButton from "../CustomButton";
import { RootState } from "../../store";
import { AccessibleClassRoom } from "../../types/schoolAccess";

interface Props {
  onClose?: () => void;
  title?: string;
  actionLabel?: string;
  onAction?: (classes: AccessibleClassRoom[]) => void;
  defaultSelectedIds?: number[];
  selectionMode?: "single" | "multiple";
}

export default function SelectClassModal({
  onClose,
  title = "Select Class",
  actionLabel = "Next",
  onAction,
  defaultSelectedIds,
  selectionMode = "multiple",
}: Props) {
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>(
    defaultSelectedIds || [],
  );

  const { accessibleClassRooms, isLoading } = useSelector(
    (state: RootState) => state.access,
  );

  useEffect(() => {
    if (defaultSelectedIds) {
      setSelectedClassIds(defaultSelectedIds);
    }
  }, [defaultSelectedIds]);

  const toggleSelectAll = () => {
    if (selectionMode === "single") return;

    if (selectedClassIds.length === accessibleClassRooms?.length) {
      setSelectedClassIds([]);
    } else {
      setSelectedClassIds(accessibleClassRooms?.map((c) => c.classId));
    }
  };

  const toggleRow = (classId: number) => {
    if (selectionMode === "single") {
      setSelectedClassIds([classId]);
    } else {
      setSelectedClassIds((prev) =>
        prev.includes(classId)
          ? prev.filter((id) => id !== classId)
          : [...prev, classId],
      );
    }
  };

  const isAllSelected =
    selectionMode === "multiple" &&
    accessibleClassRooms.length > 0 &&
    selectedClassIds.length === accessibleClassRooms.length;

  const columns: Column<AccessibleClassRoom>[] = [
    {
      label:
        selectionMode === "multiple" ? (
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
          />
        ) : (
          ""
        ),
      accessor: "select",
      render: (item) =>
        selectionMode === "single" ? (
          <input
            type="checkbox"
            name="class-select"
            checked={selectedClassIds.includes(item.classId)}
            onChange={() => toggleRow(item.classId)}
          />
        ) : (
          <input
            type="checkbox"
            checked={selectedClassIds.includes(item.classId)}
            onChange={() => toggleRow(item.classId)}
          />
        ),
    },
    // { label: "SL NO.", accessor: "classId" },
    {
      label: "Class Name",
      accessor: "className",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.className}
        </div>
      ),
    },
  ];

  return (
    <ModalLayout onClose={onClose} width="max-w-sm">
      <div className="flex flex-col gap-6 max-h-96 overflow-y-auto custom-scrollbar">
        <span className="font-semibold text-xl text-neutral-400">{title}</span>

        <DataTable
          columns={columns}
          data={accessibleClassRooms}
          isLoading={isLoading}
        />

        <div className="flex gap-4 justify-end">
          <CustomButton
            text="Cancel"
            color="bg-neutral-200"
            onClick={onClose}
          />

          <CustomButton
            text={actionLabel}
            color="bg-primary-500"
            disabled={selectedClassIds.length === 0}
            onClick={() => {
              const selectedClasses = accessibleClassRooms.filter((c) =>
                selectedClassIds.includes(c.classId),
              );
              onAction?.(selectedClasses);
            }}
          />
        </div>
      </div>
    </ModalLayout>
  );
}
